import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { serviceService } from '../../services/serviceService';

// Async thunks
export const fetchServices = createAsyncThunk(
  'services/fetchServices',
  async (_, { rejectWithValue }) => {
    try {
      const services = await serviceService.getAll();
      return services;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la încărcarea serviciilor');
    }
  }
);

export const fetchServiceById = createAsyncThunk(
  'services/fetchServiceById',
  async (id, { rejectWithValue }) => {
    try {
      const service = await serviceService.getById(id);
      return service;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Serviciul nu a fost găsit');
    }
  }
);

export const createService = createAsyncThunk(
  'services/createService',
  async (serviceData, { rejectWithValue }) => {
    try {
      const service = await serviceService.create(serviceData);
      return service;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la crearea serviciului');
    }
  }
);

export const updateService = createAsyncThunk(
  'services/updateService',
  async ({ id, serviceData }, { rejectWithValue }) => {
    try {
      const service = await serviceService.update(id, serviceData);
      return service;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la actualizarea serviciului');
    }
  }
);

export const deleteService = createAsyncThunk(
  'services/deleteService',
  async (id, { rejectWithValue }) => {
    try {
      await serviceService.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la ștergerea serviciului');
    }
  }
);

export const searchServices = createAsyncThunk(
  'services/searchServices',
  async (searchTerm, { rejectWithValue }) => {
    try {
      const services = await serviceService.search(searchTerm);
      return services;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la căutarea serviciilor');
    }
  }
);

const initialState = {
  services: [],
  selectedService: null,
  loading: false,
  error: null,
  searchResults: [],
  searchLoading: false,
};

const serviceSlice = createSlice({
  name: 'services',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedService: (state) => {
      state.selectedService = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch services
      .addCase(fetchServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.loading = false;
        state.services = action.payload;
      })
      .addCase(fetchServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch service by ID
      .addCase(fetchServiceById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServiceById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedService = action.payload;
      })
      .addCase(fetchServiceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create service
      .addCase(createService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createService.fulfilled, (state, action) => {
        state.loading = false;
        state.services.push(action.payload);
      })
      .addCase(createService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update service
      .addCase(updateService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateService.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.services.findIndex(service => service.id === action.payload.id);
        if (index !== -1) {
          state.services[index] = action.payload;
        }
        if (state.selectedService?.id === action.payload.id) {
          state.selectedService = action.payload;
        }
      })
      .addCase(updateService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete service
      .addCase(deleteService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteService.fulfilled, (state, action) => {
        state.loading = false;
        state.services = state.services.filter(service => service.id !== action.payload);
      })
      .addCase(deleteService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Search services
      .addCase(searchServices.pending, (state) => {
        state.searchLoading = true;
      })
      .addCase(searchServices.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchServices.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSelectedService, clearSearchResults } = serviceSlice.actions;
export default serviceSlice.reducer;